import React, { useEffect } from 'react';
import { Building2, Briefcase, Stethoscope, Activity, TrendingUp, Home, User, Wallet, ArrowRight, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import PageHero from '../components/PageHero';

const LoanTypes = () => {
  useEffect(() => {
    document.title = 'Loan Types | Personal, Business, Hospital, Medical Equipment Loans | Digi Loans Bazaar';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Explore all loan types offered by Digi Loans Bazaar: Personal Loans, Business Loans, Home Loans, Hospital Project Financing, Medical Equipment Loans, Working Capital, and Doctor Loans in Hyderabad.');
    }
    window.scrollTo(0, 0);
  }, []);

  const loanTypes = [
    {
      icon: Building2,
      title: 'Hospital Project Financing',
      description: 'Complete financing up to ₹10 Crore for hospital setup including equipment, infrastructure, and interiors.',
      features: ['Up to 100% Financing', '7-Year Tenure', 'No Collateral*'],
      color: 'from-blue-600 to-blue-700'
    },
    {
      icon: Briefcase,
      title: 'Business Loan',
      description: 'Working capital, expansion funding, startup loans, MSME loans for businesses of all sizes.',
      features: ['Quick Approval', 'Flexible Tenure', 'Unsecured Options'],
      color: 'from-purple-600 to-purple-700'
    },
    {
      icon: Stethoscope,
      title: 'Doctor Loan',
      description: 'Special funding for doctors, clinics, diagnostic centers, and nursing homes.',
      features: ['Customized Terms', 'Low Interest', 'Fast Processing'],
      color: 'from-green-600 to-green-700'
    },
    {
      icon: Activity,
      title: 'Medical Equipment Loans & Refinance',
      description: 'Finance MRI, CT Scan, Ultrasound, X-Ray systems, and refinance existing equipment.',
      features: ['New + Used Equipment', 'Refinance Available', 'Up to 100%'],
      color: 'from-red-600 to-red-700'
    },
    {
      icon: TrendingUp,
      title: 'Working Capital & OD',
      description: 'Cash credit, overdraft facilities, and working capital loans for business growth.',
      features: ['CC/OD Facility', 'Renewable Limits', 'Quick Disbursal'],
      color: 'from-orange-600 to-orange-700'
    },
    {
      icon: User,
      title: 'Personal Loan',
      description: 'Personal loans for medical emergencies, weddings, travel, education, or any personal need.',
      features: ['No Collateral', 'Quick Approval', 'Flexible EMI'],
      color: 'from-pink-600 to-pink-700'
    },
    {
      icon: Home,
      title: 'Home Loan',
      description: 'Buy your dream home with competitive interest rates and long repayment tenure.',
      features: ['Up to 90% LTV', 'Up to 30 Years', 'Tax Benefits'],
      color: 'from-cyan-600 to-cyan-700'
    },
    {
      icon: Wallet,
      title: 'Loan Against Property (LAP)',
      description: 'Unlock the value of your property with low-interest loans for business or personal needs.',
      features: ['High Loan Amount', 'Lower Interest', 'Long Tenure'],
      color: 'from-amber-600 to-amber-700'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-32">
        <PageHero
          badge="Complete Loan Solutions"
          badgeIcon={Wallet}
          title="All"
          highlightWord="Loan Types"
          subtitle="From personal needs to business expansion and hospital projects - we provide tailored financing for every requirement"
          breadcrumb="Loan Types"
        />

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-7xl mx-auto">
            {/* Loan Types Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {loanTypes.map((loan, index) => {
                const Icon = loan.icon;
                return (
                  <div
                    key={index}
                    className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 overflow-hidden"
                  >
                    <div className={`h-2 bg-gradient-to-r ${loan.color}`}></div>
                    <div className="p-6">
                      <div className={`w-14 h-14 bg-gradient-to-br ${loan.color} rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                        {loan.title}
                      </h2>
                      <p className="text-gray-600 mb-4 text-sm leading-relaxed">{loan.description}</p>
                      <div className="space-y-2 pt-4 border-t border-gray-100">
                        {loan.features.map((feature, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <div className="mt-16 bg-gradient-to-br from-blue-900 to-blue-800 rounded-3xl p-8 md:p-12 text-center text-white shadow-2xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Need a Custom Loan Solution?
              </h2>
              <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
                Our experts will help you find the perfect financing option tailored to your specific needs
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/#contact"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold px-8 py-3 rounded-lg transition-all hover:scale-105 shadow-lg"
                >
                  Apply Now <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href="tel:+919703850009"
                  className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 font-semibold px-8 py-3 rounded-lg transition-all hover:scale-105 shadow-lg"
                >
                  📞 Call +91 9703850009
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default LoanTypes;
